// pages/EditProfile.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { useAuth } from "../context/AuthContext";
import AVATARS, { getAvatarUrl } from "../constants/avatars";
import "../styles/pages/_editprofile.css";

// Avatar Picker Modal

function AvatarPickerModal({ current, onSelect, onClose }) {
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(current);

    const filtered = AVATARS.filter((av) =>
        av.label.toLowerCase().includes(search.toLowerCase())
    );

    // Close on Escape
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    // Prevent body scroll while modal is open
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    const handleConfirm = () => {
        onSelect(selected);
    };

    return (
        <div className="ep-modal-overlay" onClick={onClose}>
            <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ep-modal-header">
                    <h2 className="ep-modal-title">Choose Your Avatar</h2>
                    <button
                        className="ep-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        x
                    </button>
                </div>

                <div className="ep-modal-search">
                    <input
                        className="ep-input"
                        type="text"
                        placeholder="Search avatars…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                </div>

                <div className="ep-modal-grid-wrap">
                    {filtered.length === 0 ? (
                        <p className="ep-modal-empty">No avatars match "{search}"</p>
                    ) : (
                        <div className="ep-modal-grid">
                            {filtered.map((av) => (
                                <button
                                    key={av.id}
                                    type="button"
                                    className={`ep-modal-option${
                                        selected === av.file
                                            ? " ep-modal-option--selected"
                                            : ""
                                    }`}
                                    onClick={() => setSelected(av.file)}
                                    title={av.label}
                                >
                                    <img
                                        src={getAvatarUrl(av.file)}
                                        alt={av.label}
                                        loading="lazy"
                                    />
                                    <span className="ep-modal-option-label">
                                        {av.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="ep-modal-footer">
                    <div className="ep-modal-preview">
                        <img
                            src={getAvatarUrl(selected)}
                            alt="Selected"
                            className="ep-modal-preview-img"
                        />
                        <span className="ep-modal-preview-name">
                            {AVATARS.find((a) => a.file === selected)?.label || selected}
                        </span>
                    </div>
                    <div className="ep-modal-actions">
                        <button
                            className="ep-btn ep-btn--secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            className="ep-btn ep-btn--primary"
                            onClick={handleConfirm}
                        >
                            Select Avatar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function EditProfile() {
    const { user } = useAuth();
    const navigate  = useNavigate();

    const [form, setForm] = useState({
        display_name: "",
        bio: "",
        avatar_url: "default.png",
        signature: "",
        title: "",
    });
    const [original, setOriginal]       = useState(null);
    const [loading, setLoading]         = useState(true);
    const [saving, setSaving]           = useState(false);
    const [error, setError]             = useState("");
    const [success, setSuccess]         = useState("");
    const [showPicker, setShowPicker]   = useState(false);

    // Load current profile data
    useEffect(() => {
        apiFetch("/api/profiles/me/edit")
            .then((data) => {
                const p = data.profile;
                const loaded = {
                    display_name: p.display_name || "",
                    bio: p.bio || "",
                    avatar_url: p.avatar_url || "default.png",
                    signature: p.signature || "",
                    title: p.title || "",
                };
                setForm(loaded);
                setOriginal(loaded);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const hasChanges = original && JSON.stringify(form) !== JSON.stringify(original);

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setSuccess("");
    };

    const handleAvatarSelect = (file) => {
        setForm((prev) => ({ ...prev, avatar_url: file }));
        setShowPicker(false);
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!hasChanges) return;

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            await apiFetch("/api/profiles/me", {
                method: "PUT",
                body: JSON.stringify(form),
            });
            setOriginal({ ...form });
            setSuccess("Profile updated successfully!");
        } catch (err) {
            setError(err.message || "Failed to update profile");
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges && !window.confirm("Discard unsaved changes?")) return;
        navigate(`/profile/${user.id}`);
    };

    if (loading) {
        return <div className="ep-loading">Loading your profile…</div>;
    }

    return (
        <div className="ep-root">
            <h1 className="ep-heading">Edit Profile</h1>

            {error && <div className="ep-alert ep-alert--error">{error}</div>}
            {success && <div className="ep-alert ep-alert--success">✓ {success}</div>}

            <div className="ep-layout">
                {/* Live Preview */}
                <aside className="ep-preview">
                    <h2 className="ep-preview-title">Preview</h2>
                    <div className="ep-preview-card">
                        <img
                            className="ep-preview-avatar"
                            src={getAvatarUrl(form.avatar_url)}
                            alt="Avatar preview"
                        />
                        <h2 className="ep-preview-name">
                            {form.display_name || user?.username || "Username"}
                        </h2>
                        {form.title && (
                            <span className="ep-preview-usertitle">{form.title}</span>
                        )}
                        {form.bio && (
                            <p className="ep-preview-bio">{form.bio}</p>
                        )}
                        {form.signature && (
                            <div className="ep-preview-sig">
                                <hr />
                                <p>{form.signature}</p>
                            </div>
                        )}
                    </div>
                </aside>

                {/* Form */}
                <form className="ep-form" onSubmit={handleSubmit}>
                    {/* Avatar */}
                    <div className="ep-field">
                        <label className="ep-label">Profile Picture</label>
                        <div className="ep-avatar-current">
                            <img
                                className="ep-avatar-img"
                                src={getAvatarUrl(form.avatar_url)}
                                alt="Current avatar"
                            />
                            <button
                                type="button"
                                className="ep-btn ep-btn--secondary"
                                onClick={() => setShowPicker(true)}
                            >
                                Change Avatar
                            </button>
                        </div>
                    </div>

                    {/* Display Name */}
                    <div className="ep-field">
                        <label className="ep-label" htmlFor="display_name">
                            Display Name
                        </label>
                        <input
                            id="display_name"
                            className="ep-input"
                            type="text"
                            maxLength={30}
                            placeholder={user?.username || "Your display name"}
                            value={form.display_name}
                            onChange={handleChange("display_name")}
                        />
                        <span className="ep-hint">
                            {form.display_name.length}/30 — Leave blank to use your username
                        </span>
                    </div>

                    {/* Title */}
                    <div className="ep-field">
                        <label className="ep-label" htmlFor="title">
                            Title
                        </label>
                        <input
                            id="title"
                            className="ep-input"
                            type="text"
                            maxLength={100}
                            placeholder="e.g. Legendary Lord, Chaos Undivided…"
                            value={form.title}
                            onChange={handleChange("title")}
                        />
                        <span className="ep-hint">{form.title.length}/100</span>
                    </div>

                    {/* Bio */}
                    <div className="ep-field">
                        <label className="ep-label" htmlFor="bio">
                            Bio / Description
                        </label>
                        <textarea
                            id="bio"
                            className="ep-textarea"
                            maxLength={400}
                            rows={5}
                            placeholder="Tell others about yourself…"
                            value={form.bio}
                            onChange={handleChange("bio")}
                        />
                        <span className="ep-hint">{form.bio.length}/400</span>
                    </div>

                    {/* Signature */}
                    <div className="ep-field">
                        <label className="ep-label" htmlFor="signature">
                            Signature
                        </label>
                        <textarea
                            id="signature"
                            className="ep-textarea"
                            maxLength={100}
                            rows={3}
                            placeholder="Shown below your posts…"
                            value={form.signature}
                            onChange={handleChange("signature")}
                        />
                        <span className="ep-hint">{form.signature.length}/100</span>
                    </div>

                    {/* Buttons */}
                    <div className="ep-actions">
                        <button
                            type="submit"
                            className="ep-btn ep-btn--primary"
                            disabled={!hasChanges || saving}
                        >
                            {saving ? "Saving…" : "Save Changes"}
                        </button>
                        <button
                            type="button"
                            className="ep-btn ep-btn--secondary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            {/* Avatar Picker Modal */}
            {showPicker && (
                <AvatarPickerModal
                    current={form.avatar_url}
                    onSelect={handleAvatarSelect}
                    onClose={() => setShowPicker(false)}
                />
            )}
        </div>
    );
}